import React, { useEffect, useRef, useState } from "react";
import {
  Link,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  formatDate,
  getOrderStatusBadge,
  getPaymentStatusBadge,
} from "../../helper/Helper";

import moment from "moment";
import orderAPI from "../../api/orderApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import RowsPerPage from "../../components/partials/RowsPerPage";
import Pagination from "../../components/partials/Pagination";

function List() {
  const inputRef = useRef();
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [deliveredCount, setDeliveredCount] = useState("0");
  const [processingCount, setProcessingCount] = useState("0");
  const [shippedCount, setShippedCount] = useState("0");
  const [totalRevenue, setTotalRevenue] = useState("0");

  const [orderData, setOrderData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  const pageTitle = "Order List";
  const [newStatus, setNewStatus] = useState("");

  const { id } = useParams();

  useEffect(() => {
    fetchCustomerOrder();
  }, [id]);

  const fetchCustomerOrder = async () => {
    try {
      const res = await orderAPI.list({ customerId: id });

      if (res.code === 200) {
        const result = res.data;

        setOrderData(result.orders);
        setDeliveredCount(result.deliveredCount);
        setProcessingCount(result.processingCount);
        setShippedCount(result.shippedCount);
        setPagination(result.pagination);
        setTotalRevenue(result.totalrevenue);
      }
    } catch (err) {}
  };

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  useEffect(() => {
    if (!inputRef.current) return;

    const $input = window.$(inputRef.current);
    const start = moment().subtract(6, "days");
    const end = moment();

    function booking_range(start, end) {
      const formatted = `${start.format("YYYY-MM-DD")} to ${end.format(
        "YYYY-MM-DD",
      )}`;
      $input.val(formatted);
      updateFilter("daterange", formatted);
    }

    $input.daterangepicker(
      {
        autoUpdateInput: false,
        startDate: start,
        endDate: end,
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [
            moment().subtract(1, "days"),
            moment().subtract(1, "days"),
          ],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        },
      },
      booking_range,
    );

    $input.on("apply.daterangepicker", function (ev, picker) {
      const value = `${picker.startDate.format(
        "YYYY-MM-DD",
      )} to ${picker.endDate.format("YYYY-MM-DD")}`;
      $input.val(value);
      updateFilter("daterange", value);
    });

    $input.on("cancel.daterangepicker", function () {
      $input.val("");
      updateFilter("daterange", "");
    });

    // Set initial value if exists
    const currentValue = searchParams.get("daterange");
    if (currentValue) {
      $input.val(currentValue);
      const [startStr, endStr] = currentValue.split(" to ");
      const start = moment(startStr, "YYYY-MM-DD");
      const end = moment(endStr, "YYYY-MM-DD");
      $input.data("daterangepicker").setStartDate(start);
      $input.data("daterangepicker").setEndDate(end);
    }

    return () => {
      $input.data("daterangepicker")?.remove();
    };
  }, [updateFilter, searchParams]);

  const handleSelectAll = () => {
    if (!selectAll) {
      const allIds = orderData.map((order) => order._id);
      setSelectedOrders(allIds);
    } else {
      setSelectedOrders([]);
    }
    setSelectAll(!selectAll);
  };

  const handleSelectOne = (id) => {
    let newSelected;

    if (selectedOrders.includes(id)) {
      newSelected = selectedOrders.filter((item) => item !== id);
    } else {
      newSelected = [...selectedOrders, id];
    }

    setSelectedOrders(newSelected);
    setSelectAll(newSelected.length === orderData.length);
  };

  const updateOrders = async (orderIds, status) => {
    if (!status) {
      alert("Please select a status to update.");
      return;
    }

    const payload = {
      orders: orderIds,
      status,
    };

    const res = await orderAPI.updateStatus(payload);
    if (res.code === 200) {
      showSuccess(res.message);
      setSelectedOrders([]);
    } else {
      showError(res.message);
    }
  };

  return (
    <div className="content">
      <div className="page-header">
        <div className="add-item d-flex">
          <div className="page-title">
            <h4>Orders</h4>
            <h6>Keep track and manage your orders efficiently</h6>
          </div>
        </div>
      </div>

      <div>
        <div className="welcome align-items-center justify-content-between">
          <div className="row">
            <div className="col-md-6 d-flex align-items-center welcome-text">
              <h3 className="align-items-center">
                <img
                  src={`${process.env.PUBLIC_URL}/img/icons/hi.svg`}
                  alt="img"
                />
                &nbsp;Hi,
              </h3>
              &nbsp;<h6>here's what's happening with your store today.</h6>
            </div>
            <div className="col-md-6 d-flex">
              <div className="me-2 flex-grow-1" style={{ minWidth: "220px" }}>
                <div className="input-icon-start position-relative">
                  <span className="input-icon-addon fs-16 text-gray-9">
                    <i className="ti ti-calendar" />
                  </span>
                  <input
                    type="text"
                    ref={inputRef}
                    className="form-control date-range bookingrange"
                    placeholder="Search by Date"
                    defaultValue={searchParams.get("daterange") || ""}
                  />
                </div>
              </div>

              <div className="me-2" style={{ width: "160px" }}>
                <select
                  className="form-control"
                  value={searchParams.get("status") || ""}
                  onChange={(e) => updateFilter("status", e.target.value)}
                >
                  <option value="">Select Status</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                </select>
              </div>

              <div style={{ width: "220px" }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by Customer Name/OrderId"
                  value={searchParams.get("search") || ""}
                  onChange={(e) => updateFilter("search", e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row sales-cards">
          <div className="col-xl-3 col-sm-12 col-12 d-flex">
            <div className="card d-flex align-items-center justify-content-between flex-fill mb-4">
              <div>
                <h6>Total Earning</h6>
                <h3>
                  ₹
                  <span className="counters">
                    {totalRevenue.toLocaleString("en-IN")}
                  </span>
                </h3>
              </div>
              <img
                src={`${process.env.PUBLIC_URL}/img/icons/weekly-earning.svg`}
                alt="img"
              />
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card color-info bg-primary flex-fill mb-4">
              <div className="mb-2">
                <i className="ti ti-repeat fs-24" />
              </div>
              <h3 className="counters">{processingCount ?? ""}</h3>
              <p>New Orders</p>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card color-info bg-secondary flex-fill mb-4">
              <div className="mb-2">
                <i className="ti ti-gift fs-24" />
              </div>
              <h3 className="counters">{deliveredCount ?? ""}</h3>
              <p>Delivered Orders</p>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card color-info bg-secondary flex-fill mb-4">
              <div className="mb-2">
                <i className="ti ti-brand-pocket fs-24" />
              </div>
              <h3 className="counters">{shippedCount ?? ""}</h3>
              <p>Shipped Orders</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
          <h4>{pageTitle}</h4>

          {selectedOrders.length > 0 && (
            <div className="d-flex gap-2 align-items-center">
              <select
                className="form-select"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                style={{ width: "200px" }}
              >
                <option value="">-- Choose Status --</option>
                <option value="processing">Mark as Processing</option>
                <option value="shipped">Mark as Shipped</option>
                <option value="delivered">Mark as Delivered</option>
              </select>

              <button
                className="btn btn-primary"
                disabled={selectedOrders.length === 0 || newStatus === ""}
                onClick={() => updateOrders(selectedOrders, newStatus)}
              >
                Update Selected Orders ({selectedOrders.length})
              </button>
            </div>
          )}
        </div>
        <div className="card-body p-0">
          <div className="row">
            <div className="col-md-12">
              <div className="countablepagination p-2">
                <p>
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  )}{" "}
                  of {pagination.total} results
                </p>
              </div>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table">
              <thead className="thead-light">
                <tr>
                  <th>Order ID </th>
                  <th>Customer Details</th>
                  <th>Contact Details</th>
                  <th>Order Status</th>
                  <th>Payment Status</th>
                  <th>Order Date & Time</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orderData.map((order, index) => (
                  <tr key={order._id}>
                    <td>
                      <Link
                        to={`/orders/details/${order.orderId}`}
                        className="text-primary"
                      >
                        #{order.orderId}
                      </Link>
                    </td>
                    <td>
                      <Link to={`orders/user/${order.userId._id}`}>
                        {order.userId.firstName} {order.userId.lastName}
                      </Link>
                    </td>
                    <td>
                      <div>
                        {order?.email && (
                          <a
                            className="text-info"
                            href={`mailto:${order?.email}`}
                          >
                            {order?.email}
                          </a>
                        )}
                      </div>
                      <div>
                        {order?.phone && (
                          <a href={`tel:${order?.phone}`} className="text-info">
                            +91-{order?.phone}
                          </a>
                        )}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge ${getOrderStatusBadge(
                          order?.status,
                        )}`}
                      >
                        {order?.status}
                      </span>
                      {order?.statusHistoryAwb?.awb && (
                        <div>
                          <a
                            href={order?.statusHistoryAwb?.trackingUrl}
                            target="_blank"
                            rel="noopener noreferrrer"
                            className="btn btn-sm mt-1 btn-outline-primary"
                          >
                            Tracking Status
                          </a>
                        </div>
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge ${getPaymentStatusBadge(
                          order?.payment?.status,
                        )}`}
                      >
                        {order?.payment?.status}
                      </span>
                    </td>
                    <td>
                      {order.createdAt ? formatDate(order.createdAt) : ""}
                    </td>
                    <td className="fw-medium">
                      ₹
                      {Number(order?.total || 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      /-
                    </td>
                    <td>
                      <Link
                        to={`/orders/details/${order.orderId}`}
                        className="btn btn-primary btn-sm"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row">
            <RowsPerPage
              value={searchParams.get("limit") || "10"}
              onChange={(val) => updateFilter("limit", val)}
            />

            <Pagination pagination={pagination} updateFilter={updateFilter} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default List;
